import React, { useState, useCallback, useMemo, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  CircularProgress,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { i18n } from "@lingui/core";
import { Trans } from "@lingui/react/macro";
import { msg } from "@lingui/core/macro";
import TallyButton from "../common/TallyButton.tsx";
import { useSSE } from "../../hooks.tsx";
import {
  syncIntegration,
  fetchIntegrationSyncStatus,
} from "../../utils/integrationSync.ts";
import { resolveLocalizedMessage } from "../../utils/resolveLocalizedMessage";
import { BASE_URL } from "../../const.ts";
import type { IntegrationMeta } from "./integrationMetadata.ts";
import type { SyncStatus, SSEMessage, SSEOptions } from "../../types";

interface IntegrationDetailModalProps {
  open: boolean;
  onClose: () => void;
  integration: IntegrationMeta;
  isConnected: boolean;
  onConnect?: () => void;
  onStartAnimation?: () => void;
  onStopAnimation?: () => void;
}

const formatDate = (utcString: string) => {
  if (!utcString)
    return i18n._(
      msg({ context: "Fallback when date is not available", message: "N/A" }),
    );
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(utcString));
};

const IntegrationDetailModal: React.FC<IntegrationDetailModalProps> = ({
  open,
  onClose,
  integration,
  isConnected,
  onConnect,
  onStartAnimation,
  onStopAnimation,
}) => {
  const { Icon, iconSrc, name, syncable } = integration;
  const [syncStatus, setSyncStatus] = useState<SyncStatus | null>(null);
  const [isLoadingStatus, setIsLoadingStatus] = useState(false);
  const [awaitingSync, setAwaitingSync] = useState(false);
  const [syncMessages, setSyncMessages] = useState<string[]>([]);
  const [sseUrl, setSseUrl] = useState<string | null>(null);

  // Fetch sync status when modal opens for a connected + syncable integration
  useEffect(() => {
    if (!open || !isConnected || !syncable) return;
    let cancelled = false;
    setIsLoadingStatus(true);
    fetchIntegrationSyncStatus(integration.identifier)
      .then((status) => {
        if (!cancelled) setSyncStatus(status);
      })
      .catch((err) =>
        console.error(
          `Failed to fetch sync status for ${integration.identifier}:`,
          err,
        ),
      )
      .finally(() => {
        if (!cancelled) setIsLoadingStatus(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, isConnected, syncable, integration.identifier]);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setSyncStatus(null);
      setAwaitingSync(false);
      setSyncMessages([]);
      setSseUrl(null);
      onStopAnimation?.();
    }
  }, [open, onStopAnimation]);

  // SSE options for sync streaming
  const sseOptions = useMemo<SSEOptions>(
    () => ({
      onMessage: (data: SSEMessage) => {
        if (data.type === "status") {
          setSyncStatus(data.data);
          const messageText = data.data.message || data.data.body;
          if (messageText) {
            const text =
              typeof messageText === "string"
                ? messageText
                : resolveLocalizedMessage(i18n, messageText);
            setSyncMessages((prev) => [...prev, text]);
          }
        } else if (data.type === "completed" || data.type === "stream_ended") {
          fetchIntegrationSyncStatus(integration.identifier)
            .then((status) => {
              if (status) setSyncStatus(status);
            })
            .catch((err) =>
              console.error(
                `Failed to re-fetch sync status for ${integration.identifier}:`,
                err,
              ),
            );
          setSseUrl(null);
          setAwaitingSync(false);
          setSyncMessages([]);
          onStopAnimation?.();
        } else if (data.type === "error") {
          setSseUrl(null);
          setAwaitingSync(false);
          setSyncMessages([]);
          onStopAnimation?.();
        }
      },
      onError: () => {
        setSseUrl(null);
        setAwaitingSync(false);
        setSyncMessages([]);
        onStopAnimation?.();
      },
    }),
    [integration.identifier, onStopAnimation],
  );

  const { closeConnection } = useSSE(sseUrl, sseOptions);

  useEffect(() => {
    return () => closeConnection();
  }, [closeConnection]);

  const handleSyncNow = useCallback(async () => {
    setAwaitingSync(true);
    setSyncMessages([]);
    onStartAnimation?.();
    try {
      const syncResponse = await syncIntegration(integration.identifier);
      const { signed_value: signedValue, sync_id: syncId } = syncResponse.data;
      if (!signedValue || !syncId) {
        setAwaitingSync(false);
        return;
      }
      setSseUrl(
        `${BASE_URL}/v1/integrations/sync/stream/${integration.identifier}/?user_signature=${encodeURIComponent(signedValue)}&sync_id=${encodeURIComponent(syncId)}`,
      );
    } catch (error) {
      console.error(`Error syncing ${integration.identifier}:`, error);
      setAwaitingSync(false);
      onStopAnimation?.();
    }
  }, [integration.identifier, onStartAnimation, onStopAnimation]);

  const handleClose = useCallback(() => {
    onStopAnimation?.();
    onClose();
  }, [onClose, onStopAnimation]);

  const showSyncButton = isConnected && syncable && !awaitingSync;
  const showConnectButton = !isConnected && syncable && !!onConnect;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          p: 1,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
          pb: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          {/* Icon */}
          <Box
            sx={{
              width: 44,
              height: 44,
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "10px",
              backgroundColor: "rgba(169, 125, 88, 0.1)",
            }}
          >
            {Icon ? (
              <Icon sx={{ fontSize: 28, color: "primary.main" }} />
            ) : iconSrc ? (
              <img src={iconSrc} alt={name} style={{ width: 28, height: 28 }} />
            ) : null}
          </Box>
          <Box>
            <Typography variant="h6" fontWeight="bold" color="text.primary">
              {name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {i18n._(integration.description)}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {showSyncButton && (
            <TallyButton onClick={handleSyncNow}>
              <Trans context="Button to trigger manual integration sync">
                Sync now
              </Trans>
            </TallyButton>
          )}
          {showConnectButton && (
            <TallyButton onClick={onConnect}>
              <Trans context="Button to initiate connecting an integration">
                Connect
              </Trans>
            </TallyButton>
          )}
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        {/* Connection status */}
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="subtitle2"
            color="text.secondary"
            sx={{ mb: 0.5 }}
          >
            <Trans context="Label for integration connection status section">
              Status
            </Trans>
          </Typography>
          <Chip
            size="small"
            label={
              isConnected ? (
                <Trans context="Integration connected status in detail modal">
                  Connected
                </Trans>
              ) : (
                <Trans context="Integration not connected status in detail modal">
                  Available
                </Trans>
              )
            }
            sx={{
              backgroundColor: isConnected
                ? "rgba(46, 125, 50, 0.1)"
                : "rgba(0, 0, 0, 0.06)",
              color: isConnected ? "#2e7d32" : "text.secondary",
              fontWeight: 500,
            }}
          />
        </Box>

        {/* Sync status (for connected + syncable integrations) */}
        {isConnected && syncable && (
          <Box>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ mb: 0.5 }}
            >
              <Trans context="Label for last sync time section">
                Last sync
              </Trans>
            </Typography>

            {isLoadingStatus ? (
              <CircularProgress size={20} />
            ) : awaitingSync ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  minHeight: 60,
                }}
              >
                {syncMessages.length > 0 ? (
                  <Box>
                    {syncMessages.map((message, index) => (
                      <Typography
                        key={index}
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 0.5 }}
                      >
                        {index > 0 && "✓ "}
                        {message}
                      </Typography>
                    ))}
                  </Box>
                ) : (
                  <CircularProgress size={20} />
                )}
              </Box>
            ) : (
              <Typography variant="body2" color="text.primary">
                {syncStatus?.last_successful_sync_end
                  ? formatDate(syncStatus.last_successful_sync_end)
                  : i18n._(
                      msg({
                        context: "Message when no sync has been performed yet",
                        message: "No sync recorded",
                      }),
                    )}
              </Typography>
            )}

            {/* Error info */}
            {syncStatus?.last_error_traceback && !awaitingSync && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" color="error">
                  {`${i18n._(
                    msg({
                      context: "Label before error message in sync status",
                      message: "Error:",
                    }),
                  )} ${syncStatus.last_error_traceback}`}
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* Google Sheets: no sync, show info */}
        {!syncable && (
          <Box>
            <Typography variant="body2" color="text.secondary">
              <Trans context="Info about Google Sheets connection management">
                Connection is managed via the Google Sheets drawer action.
              </Trans>
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default IntegrationDetailModal;
